import { ApolloClient, createHttpLink, gql } from '@apollo/client';
import { InMemoryCache, PossibleTypesMap } from '@apollo/client/cache';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import fetch from 'cross-fetch';

import possibleTypes from '../graphql.schema.json';
import Cookies from '../utils/cookies';

declare global {
  interface Window {
    __APOLLO_STATE__: any;
    videoEvents: any;
    verseEvents: any;
  }
}

window.__APOLLO_STATE__ = window.__APOLLO_STATE__ || {};

/**
 * Extracts `PossibleTypesMap` as accepted by `@apollo/client` from GraphQL introspection query result
 */
const introspectionToPossibleTypes = (introspectionQueryResultData): PossibleTypesMap => {
  const possibleTypes = {};

  introspectionQueryResultData.__schema.types.forEach((supertype) => {
    if (supertype.possibleTypes) {
      possibleTypes[supertype.name] = supertype.possibleTypes.map((subtype) => subtype.name);
    }
  });

  return possibleTypes;
};

const createApolloClient = (
  handleError,
  serverCookies = undefined,
  // eslint-disable-next-line no-unused-vars
  host = '',
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  userAgent = '',
  getToken,
) => {
  const ssrMode = !!serverCookies;
  let cookies;
  if (ssrMode) {
    cookies = {
      // @ts-ignore
      get: (name) => serverCookies && serverCookies[name],
    };
  } else {
    cookies = Cookies;
  }

  const windowHostname = window?.location?.hostname;
  const headers = {
    'client-referer': windowHostname || host,
  };

  const httpLink = createHttpLink({
    uri: process.env.REACT_APP_API_URL,
    credentials: 'same-origin',
    headers,
    fetch,
  });

  const authLink = setContext(async (_, { headers }) => {
    // get the authentication token from local storage if it exists
    let token;
    if (getToken) {
      try {
        token = await getToken();
      } catch (e) {}
    }

    // return the headers to the context so httpLink can read them
    return {
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : null,
      },
    };
  });

  const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
    if (graphQLErrors) {
      let isUNAUTHORIZED = false;
      graphQLErrors.forEach(({ message }) => {
        if (message.includes('UNAUTHORIZED')) {
          isUNAUTHORIZED = true;
        }
      });
      if (isUNAUTHORIZED) {
        if (ssrMode) {
          cookies.clear();
          return handleError(graphQLErrors, operation);
        }
        try {
          const oldHeaders = operation.getContext().headers;
          if (oldHeaders && oldHeaders.retried) {
            return handleError(graphQLErrors, operation);
          }
          operation.setContext({
            headers: {
              ...oldHeaders,
              // authorization: handleRefreshToken(),
              retried: 'true',
            },
          });
          return forward(operation);
        } catch (err) {
          cookies.clear();
          localStorage.clear();
          return handleError(graphQLErrors, operation);
        }
      } else {
        return handleError(graphQLErrors, operation);
      }
    }
    if (networkError) {
      return;
    }
  });

  const cache = ssrMode
    ? new InMemoryCache({ possibleTypes: introspectionToPossibleTypes(possibleTypes) })
    : new InMemoryCache({
        possibleTypes: introspectionToPossibleTypes(possibleTypes),
        typePolicies: {
          Query: {
            fields: {
              getGeneralListAssignmentsManager: {
                keyArgs: false,
                merge(existing, incoming) {
                  if (
                    incoming?.paginatorInfo?.currentPage <= existing?.paginatorInfo?.currentPage
                  ) {
                    return incoming;
                  }

                  const existingData = existing?.data || [];
                  const incomingData = incoming?.data || [];
                  const mergedData = [...existingData, ...incomingData];

                  return { ...incoming, data: mergedData };
                },
              },
            },
          },
        },
      }).restore(window.__APOLLO_STATE__);

  cache.writeQuery({
    query: gql`
      query GetHasNewsInit {
        hasNews
      }
    `,
    data: {
      hasNews: true,
    },
  });

  return new ApolloClient({
    ssrMode,
    link: errorLink.concat(authLink.concat(httpLink)),
    ssrForceFetchDelay: 1000,
    cache,
    resolvers: {},
  });
};

export default createApolloClient;
